// The "analyzing your library" state for the Deep focus tier — a scanning film
// strip, a live progress bar, and content-shaped shimmer skeletons that morph
// into the real genre/director layout. Animations respect reduced-motion and are
// disabled in test mode (§13.9), so screenshots stay stable.

export function Analyzing({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  return (
    <div data-testid="enriched-skeleton" className="mt-4">
      {/* Scanning film strip */}
      <div className="relative mb-4 h-12 overflow-hidden rounded-lg border border-border-strong bg-paper-50">
        <div className="filmroll absolute inset-y-0 left-0 flex items-center gap-2 px-2">
          {Array.from({ length: 24 }).map((_, i) => (
            <span key={i} className="h-7 w-8 shrink-0 rounded-sm border border-border-strong bg-paper-100" />
          ))}
        </div>
        {/* Sprocket holes top/bottom */}
        <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-between px-1">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className="mt-1 h-1.5 w-1.5 rounded-[1px] bg-paper-50/80" />
          ))}
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-between px-1">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className="mb-1 h-1.5 w-1.5 rounded-[1px] bg-paper-50/80" />
          ))}
        </div>
        {/* Scan glow sweeping across */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-gold-500/15 to-transparent" />
      </div>

      {/* Progress */}
      <div className="mb-4 flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-ink-900">
          <span className="inline-flex gap-0.5" aria-hidden>
            <Dot delay="0ms" />
            <Dot delay="150ms" />
            <Dot delay="300ms" />
          </span>
          Analyzing your library
        </span>
        <span className="tabular-nums text-ink-400">{total ? `${done}/${total}` : "…"}</span>
      </div>
      <div className="mb-6 h-2 w-full overflow-hidden rounded-full bg-paper-100">
        <div
          className="h-full rounded-full bg-gradient-to-r from-green-500 via-gold-500 to-red-600 transition-[width] duration-500"
          style={{ width: `${Math.max(4, pct)}%` }}
        />
      </div>

      {/* Content-shaped shimmer: genre bars */}
      <div className="space-y-2">
        {[0.9, 0.75, 0.6, 0.45].map((w, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="h-3 w-16 rounded shimmer" />
            <span className="h-3 flex-1 rounded shimmer" style={{ maxWidth: `${w * 100}%` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold-500"
      style={{ animationDelay: delay }}
    />
  );
}
